import { useEffect } from 'react'
import { usePage } from '@inertiajs/react'
import nl from './nl'

/**
 * Tiny gettext-style i18n. Keys ARE the English source strings, so:
 *   - untranslated text keeps working (it falls back to the key),
 *   - the code stays readable (`t('Find a pet sitter')`, not `t('nav.find')`),
 *   - adding a language is one dictionary file.
 * `{name}` placeholders are filled from `vars`. Plurals are two keys.
 *
 * The server decides the locale (see app/controllers/concerns/localization.rb)
 * and shares it as the `locale` Inertia prop; <LocaleSync> mirrors it into
 * <html lang> on client-side navigations.
 */
export type Locale = 'en' | 'nl'
export const LOCALES: { code: Locale; label: string; name: string }[] = [
  { code: 'en', label: 'EN', name: 'English' },
  { code: 'nl', label: 'NL', name: 'Nederlands' },
]

const DICTS: Record<Locale, Record<string, string>> = { en: {}, nl }

let currentLocale: Locale = 'en'

export function setCurrentLocale(locale: Locale) {
  currentLocale = locale
}

export function getCurrentLocale(): Locale {
  return currentLocale
}

export type Vars = Record<string, string | number>

function interpolate(text: string, vars?: Vars): string {
  if (!vars) return text
  return text.replace(/\{(\w+)\}/g, (m, k) => (k in vars ? String(vars[k]) : m))
}

/** Translate outside React (module helpers, formatters). Prefer useT() in components. */
export function tr(key: string, vars?: Vars): string {
  return interpolate(DICTS[currentLocale][key] ?? key, vars)
}

export type T = (key: string, vars?: Vars) => string

/**
 * Server flash messages arrive as finished English sentences. Most are fixed
 * strings and translate by exact key; the few that embed a name are matched
 * by pattern so the name survives. Unknown text is shown as-is.
 */
const FLASH_PATTERNS: [RegExp, string][] = [
  [/^(.+) added$/, '{name} added'],
  [/^(.+) updated$/, '{name} updated'],
  [/^(.+) was removed$/, '{name} was removed'],
]
export function tf(t: T, text: string | null | undefined): string {
  if (!text) return ''
  const exact = t(text)
  if (exact !== text) return exact
  for (const [re, key] of FLASH_PATTERNS) {
    const m = text.match(re)
    if (m) return t(key, { name: m[1] })
  }
  return text
}

/** Component hook: re-renders with the locale the server shared for this page. */
export function useT(): T {
  const locale = useLocale()
  return (key, vars) => interpolate(DICTS[locale][key] ?? key, vars)
}

export function useLocale(): Locale {
  const { locale } = usePage<{ locale?: string }>().props
  return locale === 'nl' ? 'nl' : 'en'
}

/** Keeps <html lang> and the module-level locale in step with the shared prop. */
export function LocaleSync() {
  const locale = useLocale()
  setCurrentLocale(locale)
  useEffect(() => {
    document.documentElement.lang = locale
  }, [locale])
  return null
}
